import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "./config";
import "./UserProfile.css";
import BuildingChart from "./BuildingChart";
import HorizontChart from "./HorizontChart";
import expandArrow from "../assets/img/expand arrow.png";
import { useAuth } from "../App";
import Loader from "./Loader";

function UserProfile() {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [selectedHouseId, setSelectedHouseId] = useState("");
  const [houses, setHouses] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  // Состояния для смены пароля
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  
  const navigate = useNavigate();
  const { logout, toggleChat, isLoading: authLoading, isAuthenticated } = useAuth();
  
  // Используем единый индикатор загрузки из AuthContext
  const loading = authLoading;

  // Список адресов для выпадающего списка
  const BUILDING_ADDRESSES = [
    { id: 98, address: "ул. Анисимова, 5, Норильск, Красноярский край" },
    { id: 120, address: "ул. Бегичева, 13, Норильск, Красноярский край" },
    { id: 249, address: "ул. Кирова, 2, Норильск, Красноярский край" },
    { id: 6, address: "Котульского проезд, 2, Норильск, Красноярский край" },
    { id: 53, address: "пр-кт Ленинский, 27 к1, Норильск, Красноярский край" },
    { id: 907, address: "пр-кт Ленинский, 27 к2, Норильск, Красноярский край" },
    { id: 27, address: "пл. Металлургов, 17, Норильск, Красноярский край" },
    { id: 946, address: "ул. Орджоникидзе, 10 с4, Норильск, Красноярский край" },
    { id: 578, address: "ул. Орджоникидзе, 7, Норильск, Красноярский край" },
    { id: 955, address: "ул. Талнахская, 10 к2, Норильск, Красноярский край" },
    { id: 771, address: "ул. Талнахская, 78, Норильск, Красноярский край" }
  ];

  useEffect(() => {
    // Если авторизация еще загружается, не делаем ничего
    if (authLoading) return;

    // Если пользователь не авторизован, перенаправляем на главную
    if (!isAuthenticated) {
      navigate("/");
      return;
    }

    // Инициализация дат
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    setDateFrom(oneDayAgo.toISOString().slice(0, 16));
    setDateTo(now.toISOString().slice(0, 16));
    
    // Загрузка списка домов для HorizontChart
    const fetchHouses = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (!token) return;
        
        const response = await fetch(`${API_URL}/Horizont/horizont_houses_list`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`
          },
          credentials: "include"
        });
        
        if (response.ok) {
          const data = await response.json();
          setHouses(data);
        }
      } catch (err) {
        console.error("Ошибка при загрузке списка домов:", err);
      }
    };
    
    // Загрузка данных пользователя
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("access_token");
        
        if (!token) {
          // Если токен отсутствует, перенаправляем на главную страницу
          navigate("/");
          return;
        }

        const response = await fetch(`${API_URL}/current`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`
          },
          credentials: "include"
        });

        if (!response.ok) {
          throw new Error("Не удалось получить данные пользователя");
        }

        const userData = await response.json();
        setUser(userData);
      } catch (error) {
        console.error("Ошибка при загрузке данных пользователя:", error);
        setError(error.message);
      }
    };

    fetchUserData();
    fetchHouses();
  }, [navigate, authLoading, isAuthenticated]);

  // Обработчик изменения выбранного здания для прогноза
  const handleBuildingChange = (event) => {
    setSelectedBuildingId(event.target.value);
  };
  
  // Обработчики для HorizontChart
  const handleHouseChange = (event) => {
    setSelectedHouseId(event.target.value);
  };
  
  const handleDateFromChange = (event) => {
    setDateFrom(event.target.value);
  };
  
  const handleDateToChange = (event) => {
    setDateTo(event.target.value);
  };
  
  // Функция для обновления данных HorizontChart
  const updateHorizontChart = () => {
    // Обновляем значения в компоненте перед обновлением графика
    if (horizontChartRef.current) {
      horizontChartRef.current.setSelectedHouseId(selectedHouseId);
      horizontChartRef.current.setDateFrom(dateFrom);
      horizontChartRef.current.setDateTo(dateTo);
      horizontChartRef.current.updateChart();
    }
  };
  
  // Создаем ref для доступа к методам HorizontChart
  const horizontChartRef = React.useRef();

  // Функция для получения строкового представления роли пользователя
  const getRoleName = (roleId) => {
    switch (roleId) {
      case 1:
        return "Пользователь";
      case 2:
        return "Аналитик";
      case 3:
        return "Модератор";
      case 4:
        return "Администратор";
      default:
        return "Неизвестная роль";
    }
  };

  // Функция для смены пароля
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Сбрасываем сообщения
    setPasswordError("");
    setPasswordSuccess("");
    
    // Проверка совпадения паролей
    if (newPassword !== confirmPassword) {
      setPasswordError("Новые пароли не совпадают");
      return;
    }
    
    try {
      const token = localStorage.getItem("access_token");
      
      if (!token) {
        navigate("/");
        return;
      }
      
      const response = await fetch(`${API_URL}/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        }),
        credentials: "include"
      });
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Не удалось сменить пароль");
      }
      
      // Очищаем поля ввода
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccess("Пароль успешно изменен");
      
      // Скрываем форму через 3 секунды после успешной смены
      setTimeout(() => {
        setShowPasswordChange(false);
        setPasswordSuccess("");
      }, 3000);
      
    } catch (error) {
      console.error("Ошибка при смене пароля:", error);
      setPasswordError(error.message || "Не удалось сменить пароль");
    }
  };

  // Функция для навигации к определенному разделу
  const navigateToSection = (sectionId) => {
    navigate("/"); // сначала переходим на главную страницу
    
    // После перехода на главную страницу дожидаемся загрузки DOM
    setTimeout(() => {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    }, 100); // небольшая задержка для гарантии загрузки DOM
  };

  if (loading) {
    return <Loader message="Загрузка данных пользователя..." />;
  }

  if (error) {
    return (
      <div className="user-profile error">
        <h2>Ошибка</h2>
        <p>{error}</p>
        <button onClick={() => navigate("/")}>Вернуться на главную</button>
      </div>
    );
  }

  return (
    <div className="user-profile-container">
      <div className="user-profile-header">
        <h1>Личный кабинет</h1>
        <button className="back-button" onClick={() => navigate("/")}>
          <img src={expandArrow} alt="Назад" className="back-arrow" />
        </button>
      </div>

      <div className="user-profile-card">
        <div className="user-avatar">
          {user?.username ? user.username.charAt(0).toUpperCase() : "U"}
        </div>
        
        <div className="user-info">
          <h2>{user?.username || "Пользователь"}</h2>
          
          <div className="user-details">
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{user?.email || "Не указан"}</span>
            </div>
            
            <div className="detail-row">
              <span className="detail-label">Роль:</span>
              <span className="detail-value role-badge">{getRoleName(user?.role_id)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="user-actions">
        <h3>Действия</h3>
        <div className="action-buttons fixed-size">
          <button className="action-button action-nav" onClick={() => navigateToSection("map")}>
            Просмотр карты
          </button>
          
          {user?.role_id >= 2 && (
            <button className="action-button action-nav" onClick={() => navigateToSection("analytics")}>
              Аналитика
            </button>
          )}
          
          {user?.role_id >= 3 && (
            <button className="action-button action-nav" onClick={() => navigateToSection("forecast")}>
              Прогнозы
            </button>
          )}
          
          {/* Кнопка для открытия чат-бота для пользователей с ролью 2 и выше */}
          {user?.role_id >= 2 && (
            <button 
              className="action-button action-nav chatbot" 
              onClick={toggleChat}
            >
              Открыть чат-бота
            </button>
          )}
          
          {user?.role_id === 4 && (
            <button className="action-button action-nav admin" onClick={() => navigate("/admin")}>
              Админ-панель
            </button>
          )}
          
          <button 
            className="action-button action-account"
            onClick={() => setShowPasswordChange(!showPasswordChange)}
          >
            Сменить пароль
          </button>
          
          <button 
            className="action-button action-account logout" 
            onClick={async () => {
              await logout();
              navigate("/");
            }}
          >
            Выйти из аккаунта
          </button>
        </div>
        
        {/* Форма смены пароля */}
        {showPasswordChange && (
          <div className="password-change-section">
            <h4>Изменение пароля</h4>
            {passwordError && <p className="password-error">{passwordError}</p>}
            {passwordSuccess && <p className="password-success">{passwordSuccess}</p>}
            
            <form onSubmit={handlePasswordChange}>
              <div className="form-group">
                <label htmlFor="current-password">Текущий пароль</label>
                <input 
                  type="password" 
                  id="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="new-password">Новый пароль</label>
                <input 
                  type="password" 
                  id="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirm-password">Подтвердите новый пароль</label>
                <input 
                  type="password" 
                  id="confirm-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              
              <div className="form-actions">
                <button type="submit" className="btn-save">Сохранить</button>
                <button 
                  type="button" 
                  className="btn-cancel"
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setPasswordError("");
                    setPasswordSuccess("");
                  }}
                >
                  Отмена
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
      
      {/* Секция с данными в реальном времени - видна только для ролей 3 и выше */}
      {user?.role_id >= 3 && (
        <div className="forecast-section">
          <div className="forecast-header">
          <h3>Данные в реальном времени</h3>
            <div className="chart-controls">
              <div className="building-selector">
                <select
                  value={selectedHouseId}
                  onChange={handleHouseChange}
                  className="building-select"
                >
                  <option value="">Выберите дом</option>
                  {houses.map(house => (
                    <option key={house.id} value={house.horizont_id}>
                      {house.address}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="date-selector">
                <div className="date-input-group">
                  <label>С:</label>
                  <input 
                    type="datetime-local" 
                    value={dateFrom}
                    onChange={handleDateFromChange}
                    className="date-input"
                  />
                </div>
                <div className="date-input-group">
                  <label>По:</label>
                  <input 
                    type="datetime-local" 
                    value={dateTo}
                    onChange={handleDateToChange}
                    className="date-input"
                  />
                </div>
                <button 
                  className="chart-legend-button active" 
                  onClick={updateHorizontChart}
                >
                  Загрузить
                </button>
              </div>
            </div>
          </div>
          <div className="user-profile-forecast chart-only">
          <div className="forecast-container">
              <HorizontChart 
                ref={horizontChartRef}
                showControls={false}
                externalHouseId={selectedHouseId}
                externalDateFrom={dateFrom}
                externalDateTo={dateTo}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Секция с прогнозом - видна только для пользователей с ролью 3 и выше */}
      {user?.role_id >= 3 && (
        <div className="forecast-section">
          <div className="forecast-header">
          <h3>Прогноз температур</h3>
            <div className="chart-controls">
              <div className="building-selector">
                <select
                  value={selectedBuildingId || ""}
                  onChange={handleBuildingChange}
                  className="building-select"
                >
                  <option value="">Выберите здание</option>
                  {BUILDING_ADDRESSES.map(building => (
                    <option key={building.id} value={building.id}>
                      {building.address}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="user-profile-forecast chart-only">
          <div className="forecast-container">
              <BuildingChart 
                buildingId={selectedBuildingId} 
                showSelector={false}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserProfile; 